import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import jwtDecode from "jwt-decode";
import { createMigrate, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import { ROLE_2FA_AUTH_REQUIRED } from "../config/roles";
import {
  AllowedStateRoles,
  Available2FAType,
  JwtPayload,
  rewritePermissionGroupNames,
  SerializedAuthUser,
} from "./utils";

import type { Customer } from "modules/customers";
import type { CurrentUser } from "modules/user-management";
import type { RootState } from "../../../store";
import type { PERMISSION_GROUP_LIST_ALL } from "../constants/permissionGroups";
import type { LoginCredentials } from "../models/LoginCredentials";

export interface SliceState {
  accessToken: null | string;
  refreshToken: null | string;
  expirationTimestamp: null | number;
  tokenExpiryMs: null | number;
  userId: null | string;
  customerId: null | string;
  customer: Customer | null;
  username: null | string;
  currentUser?: Partial<CurrentUser>;
  roles: AllowedStateRoles;
  permissions: PERMISSION_GROUP_LIST_ALL[];
  available_2fa?: Available2FAType;
  voltEmployee: boolean;
  email: string;
  error: null | string;
}

export const initialState: SliceState = {
  accessToken: null,
  refreshToken: null,
  expirationTimestamp: null,
  tokenExpiryMs: null,
  userId: null,
  username: null,
  customerId: null,
  customer: null,
  currentUser: {},
  roles: [],
  permissions: [],
  voltEmployee: false,
  email: "",
  error: null,
};

export const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAccessTokenData: (state, action: PayloadAction<SerializedAuthUser>) => {
      Object.assign(state, action.payload);
      state.error = null;
    },
    clearAccessTokenData: (state) => {
      const { email, ...clearState } = initialState;

      Object.assign(state, clearState);
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setAuthCustomer: (state, action: PayloadAction<Customer | null>) => {
      state.customer = action.payload;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    setCurrentUser2FA: (state, action) => {
      if (state.currentUser) {
        state.currentUser.twoFactorAuthentication = action.payload;
      }
    },
  },
});

export const {
  setAccessTokenData,
  clearAccessTokenData,
  setAuthCustomer,
  setEmail,
  setCurrentUser,
  setCurrentUser2FA,
} = slice.actions;

export const authOperationPaths = {
  customer: ["auth", "customer"],
};

export const login = createAction<LoginCredentials>("login");
export const logout = createAction("logout");
export const forceUpdateToken = createAction("forceUpdateToken");
export const loginSuccess = createAction("loginSuccess");
export const loginFailure = createAction("loginFailure");
export const fetchAuthCustomer = createAction("fetchAuthCustomer");
export const fetchCurrentUser = createAction("fetchCurrentUser");
export const destroySession = createAction("destroySession");
export const twoFactorCall = createAction<{ code: string }>("twoFactorCall");
export const resendCode = createAction("resendCode");

export const selectEmail = (state: RootState) => state.auth.email;
export const selectAccessToken = (state: RootState) => {
  const { accessToken, expirationTimestamp } = state.auth;

  if (accessToken && expirationTimestamp) {
    return accessToken && expirationTimestamp > Date.now() ? accessToken : null;
  }

  return null;
};
export const selectIsAuthorized = (state: RootState) => {
  if (isAt2FAStage(state)) {
    return false;
  }

  return Boolean(selectAccessToken(state));
};

export const selectRemainingTokenTimeMs = (state: any) => {
  return state.auth.expirationTimestamp - Date.now();
};
export const selectCurrentUserId = (state: RootState) => state.auth.userId;
export const selectCurrentUsername = (state: RootState) => state.auth.username;
export const selectCurrentCustomerId = (state: RootState) =>
  state.auth.customerId;
export const selectCurrentUser = (state: RootState) => state.auth?.currentUser;
export const selectCurrentUserPermissions = (state: RootState) =>
  state.auth.permissions;
export const selectRefreshToken = (state: RootState) => state.auth.refreshToken;
export const selectCurrentUserIsVoltEmployee = (state: RootState) =>
  state.auth.voltEmployee;
export const selectAuthCustomer = (state: RootState) => state.auth.customer;
export const isAt2FAStage = (state: RootState) =>
  (state.auth.roles as string[]).includes(ROLE_2FA_AUTH_REQUIRED);
export const selectAvailable2FA = (state: RootState) =>
  state.auth.available_2fa;

export const authOperationPath = {
  login: "auth.login",
  twoFactorLogin: "auth.2FA",
  currentUser: "auth.current",
};

const authPersistedReducer = persistReducer(
  {
    key: "auth",
    storage,
    whitelist: [
      "email",
      "accessToken",
      "refreshToken",
      "expirationTimestamp",
      "userId",
      "username",
      "customerId",
      "permissions",
      "roles",
      "voltEmployee",
      "available_2fa",
    ],
    blacklist: [],
    version: 2,
    migrate: createMigrate(
      {
        0: (state: any) => {
          const tokenPayload = jwtDecode<JwtPayload>(state.accessToken);

          return {
            ...state,
            voltEmployee: tokenPayload.volt_employee ?? false,
            permissions: rewritePermissionGroupNames(
              tokenPayload.permissions ?? []
            ),
          };
        },
        1: (state: any) => {
          const tokenPayload = jwtDecode<JwtPayload>(state.accessToken);

          return {
            ...state,
            roles: tokenPayload.roles
              ? tokenPayload.roles.filter(
                  (role) => role === ROLE_2FA_AUTH_REQUIRED
                )
              : [],
            available_2fa: tokenPayload.available_2fa,
          };
        },
        2: (state: any) => {
          const tokenPayload = jwtDecode<JwtPayload>(state.accessToken);

          return {
            ...state,
            username: tokenPayload.username,
          };
        },
      },
      { debug: false }
    ),
  },
  slice.reducer
);

export default authPersistedReducer;
